// CodeMirror theme tuned to the harness — zinc base, yellow-300 accent,
// JetBrains Mono. Deliberately skips prebuilt themes (one-dark, vscode,
// etc.) so we don't fight the rest of the app.

import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

const baseTheme = EditorView.theme(
  {
    '&': {
      color: '#f4f4f5',
      backgroundColor: 'transparent',
      height: '100%',
    },
    '.cm-scroller': {
      fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
      fontSize: '13px',
      lineHeight: '1.55',
    },
    '.cm-content': {
      padding: '1rem',
      caretColor: '#fde047',
    },
    '&.cm-focused': {
      outline: 'none',
    },
    '.cm-gutters': {
      backgroundColor: 'transparent',
      color: '#3f3f46',
      borderRight: '1px solid #18181b',
    },
    '.cm-lineNumbers .cm-gutterElement': {
      padding: '0 0.65rem 0 0.5rem',
      minWidth: '2ch',
      textAlign: 'right',
    },
    '.cm-activeLineGutter, .cm-activeLine': {
      backgroundColor: 'transparent',
    },
    '.cm-selectionBackground, ::selection': {
      backgroundColor: '#27272a',
    },
    '&.cm-focused .cm-selectionBackground': {
      backgroundColor: '#3f3f46',
    },
    '.cm-cursor, .cm-dropCursor': {
      borderLeftColor: '#fde047',
    },
    '.cm-placeholder': {
      color: '#52525b',
      fontStyle: 'italic',
    },
    // Voice colour chip — class referenced by voiceColorsExtension.
    '.cm-voice-chip': {
      display: 'inline-block',
      width: '0.55em',
      height: '0.55em',
      borderRadius: '50%',
      marginRight: '0.28em',
      verticalAlign: 'baseline',
      transform: 'translateY(-0.02em)',
      boxShadow: '0 0 2px rgba(0, 0, 0, 0.4)',
    },
    // Solo gutter — per-const ▶ / ■ button (playableSoloGutter).
    '.cm-solo-gutter': {
      padding: '0 4px',
    },
    '.cm-solo-gutter .cm-gutterElement': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Invisible spacer — same box-size as the real button so the gutter
    // column reserves the same width whether or not a line has a marker.
    '.cm-solo-btn-spacer': {
      display: 'inline-block',
      width: '16px',
      height: '16px',
    },
    '.cm-solo-btn': {
      all: 'unset',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '16px',
      height: '16px',
      fontSize: '9px',
      lineHeight: '1',
      color: '#71717a', // zinc-500
      border: '1px solid #27272a', // zinc-800
      borderRadius: '3px',
      background: '#18181b', // zinc-900
      transition: 'color 80ms, border-color 80ms, background 80ms',
    },
    '.cm-solo-btn:hover': {
      color: '#fde047', // yellow-300
      borderColor: '#3f3f46', // zinc-700
      background: '#27272a', // zinc-800
    },
    '.cm-solo-btn.is-playing': {
      color: '#18181b', // zinc-900
      background: '#fde047', // yellow-300
      borderColor: '#fde047',
    },
    '.cm-solo-btn.is-playing:hover': {
      background: '#facc15', // yellow-400
      borderColor: '#facc15',
    },
    '.cm-solo-btn:focus-visible': {
      outline: '1px solid #fde047',
      outlineOffset: '1px',
    },
  },
  { dark: true },
);

const strudelHighlight = HighlightStyle.define([
  { tag: t.keyword, color: '#fde047' }, // yellow-300
  { tag: [t.string, t.special(t.string)], color: '#6ee7b7' }, // emerald-300
  { tag: [t.number, t.bool, t.null], color: '#67e8f9' }, // cyan-300
  { tag: t.comment, color: '#52525b', fontStyle: 'italic' }, // zinc-600
  { tag: t.operator, color: '#a1a1aa' }, // zinc-400
  { tag: t.punctuation, color: '#71717a' }, // zinc-500
  {
    tag: [t.function(t.variableName), t.propertyName],
    color: '#fafafa', // zinc-50
  },
  { tag: t.variableName, color: '#d4d4d8' }, // zinc-300
  { tag: t.definition(t.variableName), color: '#fafafa' },
  { tag: t.typeName, color: '#c4b5fd' }, // violet-300
  { tag: t.invalid, color: '#fca5a5', textDecoration: 'underline wavy' },
]);

export const strudelTheme = [baseTheme, syntaxHighlighting(strudelHighlight)];
